import { Listbox, ListboxButton, ListboxOptions } from "@headlessui/react";
import { useTranslations } from "next-intl";

import { ColorButton } from "@/components/color-palette/ColorPalette";
import { openListboxOnEnter } from "@/components/color-palette/openListboxOnEnter";
import { colors } from "@/utils/tailwindColors";
import { twMerge } from "@/utils/twMerge";

import type { TailwindColor } from "@/utils/tailwindColors";

type CurrentColorProps = {
  color?: TailwindColor;
  className?: string;
};

export function NodeCurrentColor({ color, className }: CurrentColorProps) {
  return (
    <div
      className={twMerge(
        "rounded-full w-6 h-6 p-[3px] bg-document-bg flex items-center justify-center shadow-block dark:shadow-block-inverted",
        className,
      )}
    >
      <div
        className={twMerge("w-full h-full rounded-full", `bg-${color}-200 dark:bg-${color}-800`)}
      />
    </div>
  );
}

function NodeColorOptions({ currentColor }: { currentColor?: TailwindColor }) {
  return colors.map((color) => (
    <ColorButton
      key={color}
      value={color}
      selected={currentColor === color}
      colorClassName={`bg-${color}-200 dark:bg-${color}-800`}
    />
  ));
}

const OPTIONS_CLASS_NAME =
  "grid grid-cols-[repeat(6,max-content)] p-2.25 rounded-12 bg-block-bg shadow-md dark:shadow-block-inverted outline-none";

type GridProps = {
  color?: TailwindColor;
  onSelect: (color: TailwindColor) => void;
  className?: string;
};

export function NodeColorGrid({ color, onSelect, className }: GridProps) {
  return (
    <Listbox value={color || ""} onChange={onSelect}>
      <ListboxOptions static className={twMerge(OPTIONS_CLASS_NAME, className)}>
        <NodeColorOptions currentColor={color} />
      </ListboxOptions>
    </Listbox>
  );
}

type Props = {
  color?: TailwindColor;
  onSelect: (color: TailwindColor) => void;
  trigger?: React.ReactNode;
  triggerClassName?: string;
};

export function NodeColorSelector({
  color: currentColor,
  trigger,
  triggerClassName,
  onSelect,
}: Props) {
  const t = useTranslations("sidebar");

  return (
    <Listbox value={currentColor || ""} onChange={onSelect}>
      <ListboxButton
        className={triggerClassName}
        onKeyDown={openListboxOnEnter}
        aria-label={
          currentColor
            ? t("colorPalette.currentColor", { currentColor })
            : t("colorPalette.selectColor")
        }
      >
        {trigger || <NodeCurrentColor color={currentColor} />}
      </ListboxButton>
      <ListboxOptions
        anchor={{ to: "bottom start", padding: "10px" }}
        className={twMerge(OPTIONS_CLASS_NAME, "z-50")}
      >
        <NodeColorOptions currentColor={currentColor} />
      </ListboxOptions>
    </Listbox>
  );
}
